import { useState } from 'react';
import { ArrowTopRightOnSquareIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import { useInvoice, useVoidInvoice } from '../../hooks/useBilling';
import { useMyPermissions } from '../../hooks/useMyPermissions';
import { useToast } from '../../providers/ToastProvider';
import { apiErrorMessage } from '../../utils/apiError';
import { Drawer, DrawerSection, KVRow } from './Drawer';
import { EventTimeline } from './EventTimeline';
import { PatientLink } from './PatientLink';
import { StatusBadge } from './StatusBadge';
import { isInvoiceVoidable } from '../../services/billing';
import { formatMoneyDollars as money } from '../../utils/money';
import { useEscapeKey } from '../../hooks/useEscapeKey';

function fmtDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

function fmtDateTime(iso: string): string {
    return new Date(iso).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
}

function shortRef(id: string): string {
    return id.slice(0, 8).toUpperCase();
}

// Same link format odo-integrations writes to the Monday invoices board.
function billcomInvoiceUrl(billcomId: string): string {
    return `https://app02.us.bill.com/neo/invoice/${billcomId}`;
}

// Whole days the payment landed after the due date; 0 or negative means on time.
function daysLate(dueDate: string, paidAt: string): number {
    const msPerDay = 24 * 60 * 60 * 1000;
    return Math.floor((new Date(paidAt).getTime() - new Date(dueDate).getTime()) / msPerDay);
}

// ── Void modal (also reused by the invoices list row menu) ──────────────

export interface VoidInvoiceTarget {
    id: string;
    chargeId: string;
    invoiceNumber: string;
    amount: string;
    practiceName: string;
}

interface VoidInvoiceModalProps {
    invoice: VoidInvoiceTarget;
    onClose: () => void;
}

export function VoidInvoiceModal({ invoice, onClose }: VoidInvoiceModalProps) {
    const { showToast } = useToast();
    const { mutateAsync: voidInvoice, isPending } = useVoidInvoice();

    useEscapeKey(onClose, !isPending);
    const [reason, setReason] = useState('');
    const [issueOneTimeDiscount, setIssueOneTimeDiscount] = useState(false);

    const handleConfirm = async () => {
        try {
            await voidInvoice({
                id: invoice.id,
                chargeId: invoice.chargeId,
                body: {
                    reason: reason.trim(),
                    ...(issueOneTimeDiscount && {
                        createOneTimeDiscount: {
                            description: `One-time discount from voided ${invoice.invoiceNumber}`
                        }
                    })
                }
            });
            showToast(
                issueOneTimeDiscount
                    ? `Invoice voided, ${money(invoice.amount)} one-time discount issued`
                    : 'Invoice voided',
                'success'
            );
            onClose();
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to void invoice'), 'error');
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex justify-center items-center">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-[460px] p-6">
                <div className="flex items-start gap-3 mb-2">
                    <span className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                        <NoSymbolIcon className="h-5 w-5 text-red-600" />
                    </span>
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">
                            Void invoice {invoice.invoiceNumber}?
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                            This marks the invoice void in the database. It does not void it in
                            Bill.com, do that manually if it was already sent.
                        </p>
                    </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 flex flex-col gap-1 mt-4">
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Amount</span>
                        <span className="font-semibold text-gray-900 tabular-nums">
                            {money(invoice.amount)}
                        </span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Practice</span>
                        <span className="text-gray-800">{invoice.practiceName}</span>
                    </div>
                </div>

                <div className="mt-4">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Reason *
                    </label>
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={2}
                        autoFocus
                        placeholder="e.g. duplicate charge, patient billed separately"
                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                    />
                </div>

                <label className="flex items-start gap-2.5 mt-4 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={issueOneTimeDiscount}
                        onChange={(e) => setIssueOneTimeDiscount(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">
                        Issue a {money(invoice.amount)} one-time discount to {invoice.practiceName}
                        <span className="block text-xs text-gray-400 mt-0.5">
                            The one-time discount starts unlinked, without expiry, and can be
                            attached to a future charge.
                        </span>
                    </span>
                </label>

                <div className="flex justify-end items-center gap-3 mt-6">
                    <button
                        type="button"
                        onClick={onClose}
                        className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                    >
                        Keep invoice
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            void handleConfirm();
                        }}
                        disabled={isPending || !reason.trim()}
                        className="bg-red-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isPending ? 'Voiding…' : 'Void invoice'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Drawer ──────────────────────────────────────────────────────────────

interface InvoiceDrawerProps {
    invoiceId: string;
    onClose: () => void;
    // Swaps this drawer for the charge drawer on the hosting page.
    onOpenCharge: (chargeId: string) => void;
}

export function InvoiceDrawer({ invoiceId, onClose, onOpenCharge }: InvoiceDrawerProps) {
    const { invoice, isLoading, isError } = useInvoice(invoiceId);
    const { hasPermission } = useMyPermissions();
    const canEditBilling = hasPermission('billing.update');
    const [showVoidModal, setShowVoidModal] = useState(false);

    if (isLoading) {
        return (
            <Drawer title="Invoice" onClose={onClose}>
                <div className="flex items-center justify-center gap-2 text-gray-400 py-16">
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                    <span className="text-sm">Loading invoice…</span>
                </div>
            </Drawer>
        );
    }

    if (isError || !invoice) {
        return (
            <Drawer title="Invoice" onClose={onClose}>
                <p className="text-sm text-gray-500 py-16 text-center">Invoice not found.</p>
            </Drawer>
        );
    }

    const product = invoice.charge.productSummary ?? invoice.charge.description;
    const paidLate = invoice.paidAt ? daysLate(invoice.dueDate, invoice.paidAt) : 0;

    return (
        <>
            <Drawer
                title={invoice.invoiceNumber}
                badge={<StatusBadge status={invoice.status} />}
                subtitle={`${product ?? 'No description'} · Installment ${invoice.installmentIndex + 1} of ${invoice.charge.installments}`}
                onClose={onClose}
                // A stacked overlay owns Escape for the top layer; without this the
                // drawer's own listener fires on the same keypress and closes it too.
                closeDisabled={showVoidModal}
            >
                <DrawerSection title="Details">
                    <div className="flex flex-col">
                        <KVRow k="Practice">
                            {invoice.charge.practice.name}
                            {invoice.charge.practice.location && (
                                <span className="text-gray-400">
                                    {' '}
                                    · {invoice.charge.practice.location}
                                </span>
                            )}
                        </KVRow>
                        {invoice.charge.practice.doctorName && (
                            <KVRow k="Doctor">Dr. {invoice.charge.practice.doctorName}</KVRow>
                        )}
                        <KVRow k="Patient">
                            <PatientLink
                                patientId={invoice.charge.patientId}
                                patientName={invoice.charge.patientName}
                            >
                                {invoice.charge.patientName ?? '-'}
                            </PatientLink>
                        </KVRow>
                        <KVRow k="Due date">{fmtDate(invoice.dueDate)}</KVRow>
                        <KVRow k="Bill.com">
                            {invoice.billcomId ? (
                                <a
                                    href={billcomInvoiceUrl(invoice.billcomId)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={invoice.billcomId}
                                    className="inline-flex items-center gap-1 underline decoration-gray-300 underline-offset-2 hover:text-gray-900"
                                >
                                    Invoice in Bill.com
                                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 text-gray-400" />
                                </a>
                            ) : (
                                <span className="text-gray-400">not pushed</span>
                            )}
                        </KVRow>
                        <KVRow k="Created">{fmtDateTime(invoice.createdAt)}</KVRow>
                    </div>
                </DrawerSection>

                {/* The charge this invoice was generated from - status, what was
            sold, and how this installment fits into the total. */}
                <DrawerSection title="Original charge">
                    <button
                        type="button"
                        onClick={() => onOpenCharge(invoice.chargeId)}
                        className="w-full text-left border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors flex flex-col gap-2"
                    >
                        <div className="flex items-center justify-between gap-3">
                            <span className="font-mono text-[12px] text-gray-700 underline decoration-gray-300 underline-offset-2">
                                #{shortRef(invoice.chargeId)}
                            </span>
                            <StatusBadge status={invoice.charge.status} />
                        </div>
                        <p
                            className="text-sm text-gray-800 truncate"
                            title={
                                invoice.charge.productSummary ??
                                invoice.charge.description ??
                                undefined
                            }
                        >
                            {invoice.charge.productSummary ??
                                invoice.charge.description ??
                                'No description'}
                        </p>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Charge total</span>
                            <span className="font-semibold text-gray-900 tabular-nums">
                                {money(invoice.charge.totalAmount)}
                            </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Installments</span>
                            <span className="text-gray-700">
                                {invoice.charge.installments} · this invoice is #
                                {invoice.installmentIndex + 1}
                            </span>
                        </div>
                    </button>
                </DrawerSection>

                <DrawerSection title="Amount">
                    <div className="border border-gray-200 rounded-lg p-4 flex flex-col gap-2">
                        <div className="flex justify-between">
                            <span className="text-sm font-bold text-gray-900">Invoice amount</span>
                            <span className="text-lg font-bold text-gray-900 tabular-nums">
                                {money(invoice.amount)}
                            </span>
                        </div>
                        {invoice.paidAt && (
                            <div className="flex justify-between pt-2.5 border-t border-gray-100">
                                <span className="text-sm text-gray-500">Paid</span>
                                <span className="text-sm text-green-600">
                                    {fmtDate(invoice.paidAt)}
                                    {paidLate > 0 && (
                                        <span className="text-red-500">
                                            {' '}
                                            · {paidLate} day{paidLate === 1 ? '' : 's'} late
                                        </span>
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                </DrawerSection>

                <DrawerSection title="Activity">
                    <EventTimeline events={invoice.events} />
                </DrawerSection>

                {canEditBilling && isInvoiceVoidable(invoice.status) && (
                    <div className="flex items-center pt-1 pb-2">
                        <button
                            type="button"
                            onClick={() => setShowVoidModal(true)}
                            className="flex items-center gap-2 text-red-600 font-semibold px-3 py-2 rounded-lg text-sm hover:bg-red-50 transition-colors ml-auto"
                        >
                            <NoSymbolIcon className="h-4 w-4" />
                            Void invoice
                        </button>
                    </div>
                )}
            </Drawer>

            {showVoidModal && (
                <VoidInvoiceModal
                    invoice={{
                        id: invoice.id,
                        chargeId: invoice.chargeId,
                        invoiceNumber: invoice.invoiceNumber,
                        amount: invoice.amount,
                        practiceName: invoice.charge.practice.name
                    }}
                    onClose={() => setShowVoidModal(false)}
                />
            )}
        </>
    );
}
